import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  Select,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import { MdEdit, MdVisibility } from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import ExportCsvButton from 'components/export-csv-button/ExportCsvButton';
import PatientModal from './PatientModal';
import ErrorMessage from 'components/error-message';
import { saveAs } from 'file-saver';
import { formatUSPhoneNumber } from 'utils';
import toast from 'react-hot-toast';
import { toProperCase } from 'utils/formatting';

// Column Helper
const columnHelper = createColumnHelper();

// TeamTable Component
export default function PatientsTable(props) {
  const {
    tableData,
    loading,
    fetchAgain,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    searchTerm,
    setSearchTerm,
  } = props;
  const navigate = useNavigate();
  const [type, setType] = React.useState('add');
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [selectedId, setSelectedId] = React.useState('');

  // Columns definition
  const columns = [
    columnHelper.accessor((row, index) => (currentPage - 1) * itemsPerPage + index + 1, {
      id: '#',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          #
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          NAME
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {toProperCase(info.getValue()) || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          EMAIL
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} textTransform="none" fontSize="sm" fontWeight="700">
          {(info.getValue()?.toLowerCase()) || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('phone', {
      id: 'phone',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Phone
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {formatUSPhoneNumber(info.getValue()) || "N/A"}
        </Text>
      ),
    }),
    columnHelper.accessor('insurance_name', {
      id: 'insurance_name',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Insurance Provider Name
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ACTION
        </Text>
      ),
      cell: (info) => {
        const handleViewClick = () => {
          const id = info.row.original.id; // Get the id from row data
          console.log('Navigating to details with ID:', id);

          navigate(`details/${id}`, { state: { id: id } }); // Pass ID in state
        };

        const handleEditClick = (e) => {
          const itemId = info.row.original.id; // Get the id from row data

          setSelectedId(itemId);
          onOpen();
          setType('edit');
        };

        return (
          <Box display="flex">
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit Details"
              colorScheme="brand"
              size="sm"
              mr="6px"
              onClick={handleEditClick}
            />
            <IconButton
              icon={<MdVisibility style={{ marginLeft: '-1px' }} />}
              aria-label="View Details"
              colorScheme="blackAlpha"
              size="sm"
              onClick={handleViewClick}
            />
          </Box>
        );
      },
    }),
  ];

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = React.useState([]);

  const table = useReactTable({
    data: data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  React.useEffect(() => {
    setData([...tableData]); // Ensure data is reset when tableData updates
  }, [tableData]);

  // const [searchTerm, setSearchTerm] = React.useState('');



  const itemsPerPageOptions = [10, 20, 30, 50];

  const paginatedRows = React.useMemo(() => {
    return table.getRowModel().rows;
  }, [table.getRowModel()]);



  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };


  React.useEffect(() => {
    setCurrentPage(1); // reset to first page when search term changes
  }, [searchTerm]);



  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const exportToCsv = () => {
    if (!data || data.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    const headers = ['#', 'Name', 'Email', 'Phone', 'Insurance Provider Name'];
    const rows = data.map((row, index) => [
      (currentPage - 1) * itemsPerPage + index + 1,
      row.name ?? '',
      row.email ?? '',
      row.phone ?? '',
      row.insurance_name ?? '',
    ]);

    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    saveAs(blob, 'patients.csv');
  };


  return (
    <>
      <Card flexDirection="column" w="100%" px="0px">
        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="8px"
          justify="space-between"
          align="center"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 4, md: 0 }}
        >
          <Text
            color={textColor}
            fontSize="24px"
            fontWeight="700"
            lineHeight="100%"
            mb={{ base: '8px', md: '0px' }}
          >
            Patients
          </Text>

          <Box
            w={{ base: '100%', md: 'auto' }}
            display="flex"
            justifyContent={{ base: 'center', md: 'flex-end' }}
            ml="auto"
            mr={{ md: 5 }}
          >
            <SearchBar
              width="100%"
              placeholder="Search by name, email, or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}

            />
          </Box>

          <Box w={{ base: '100%', md: 'auto' }}>
            <Button
              w={{ base: '100%', md: 'fit-content' }}
              mr={{ base: '0', md: '8px' }}
              // minW="140px"
              variant="brand"
              fontWeight="500"
              minH={"36px"}
              borderRadius="16px"
              mb={0}
              size="sm"
              onClick={() => {
                onOpen();
                setType('add');
              }}
            >
              New Patient
            </Button>
          </Box>
          {/* Hide for Now  */}
          {/* <ExportCsvButton onExport={exportToCsv} /> */}
        </Flex>

        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="8px"
          justify="space-between"
          align="center"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 4, md: 0 }}
        >
          <Box w={{ base: '100%', md: 'auto' }}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              width={{ base: '100%', md: 'fit-content' }}
              borderRadius="16px"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </Select>
          </Box>

          <Box
            w={{ base: '100%', md: 'auto' }}
            display="flex"
            justifyContent={{ base: 'center', md: 'flex-end' }}
          >
            <Pagination
              totalPages={totalPages}
              initialPage={currentPage}
              onPageChange={handlePageChange}
            />

          </Box>
        </Flex>

        <Box overflowX="auto">
          <Table variant="striped" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex justifyContent="space-between" align="center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            {/* <Tbody>
              {paginatedRows.map((row, index) => (
                <Tr key={index}>
                  {table
                    .getRowModel()
                    .rows.find((r) => r.original === row) // Ensure correct row mapping
                    ?.getVisibleCells()
                    ?.map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize="14px"
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                </Tr>
              ))}
            </Tbody> */}
            <Tbody>
              {paginatedRows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} fontSize="14px" borderColor="transparent">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>

          </Table>
        </Box>

        {!loading && data.length === 0 && (
          searchTerm.length > 0 ? (
            <ErrorMessage source="results" />
          ) : (
            <ErrorMessage source="patients" />
          )
        )}

      </Card>
      <PatientModal
        isOpen={isOpen}
        onClose={onClose}
        type={type}
        patientId={selectedId}
        fetchAgain={fetchAgain}
      />
    </>
  );
}
